import queryString from 'query-string';

import { LocaleCode, MarketCode } from '@tink/config/src/markets';
import { normalizeLocale } from '@tink/core/src/utils';

import { PISCapability } from '@tink/core/src/services/ProviderGroupsService/types';
import { ProviderCapability } from '../../models/providerCapability';
import { ajaxRetry } from '../ajaxRetry';
import { ProviderEntity, ProvidersApiResponse } from '../../models/provider';

import { base, createApiResponse, createApiURL, ResponseObj } from './base';

export type ProvidersList = {
  providers: ProviderEntity[];
};

export const convertListApiResponseToProviders = (
  response: ResponseObj<ProvidersApiResponse>
): ResponseObj<ProvidersList> => {
  const fetchedProviders = response.isOk() ? response.data.providers : [];

  return {
    ...response,
    data: {
      providers: fetchedProviders,
    },
  };
};

type ProviderQueryParams = {
  capability?: ProviderCapability;
  capabilities?: ProviderCapability;
  pisCapability?: PISCapability;
};
type ProviderListPayload = {
  market: MarketCode;
  locale: LocaleCode;
  request?: typeof ajaxRetry;
  queryParams?: ProviderQueryParams;
};

export const getProviders = async ({
  market,
  locale,
  request = ajaxRetry,
  queryParams = {},
}: ProviderListPayload): Promise<ResponseObj<ProvidersList>> => {
  const headers = base.headers();

  headers['Accept-Language'] = normalizeLocale(locale);

  return request<ProvidersApiResponse>({
    headers,
    method: 'GET',
    url: createApiURL(
      `/api/v1/providers/${market}?${queryString.stringify({
        includeTestProviders: 'true',
        ...queryParams,
      })}`
    ),
  }).then((response: ResponseObj<ProvidersApiResponse>) => {
    return convertListApiResponseToProviders(response);
  });
};

export type ProviderPayload = {
  locale: LocaleCode;
  providerName: string;
};

export const getProvider = async ({
  providerName,
  locale,
}: ProviderPayload): Promise<ResponseObj<ProviderEntity>> => {
  const headers = base.headers();

  headers['Accept-Language'] = normalizeLocale(locale);

  return ajaxRetry<ProvidersApiResponse>({
    headers,
    method: 'GET',
    url: createApiURL(`/api/v1/providers?name=${providerName}`),
  }).then((response: ResponseObj<ProvidersApiResponse>) => {
    const providersResponse = convertListApiResponseToProviders(response);

    return createApiResponse<ProviderEntity>(
      providersResponse.data.providers[0],
      providersResponse.status
    );
  });
};
